"use client";

import {
  canOpenPermissionSettings,
  getPermissionPlatform,
  type PermissionPlatform,
  type PermissionSettingsTarget,
} from "@/lib/native/permission-settings";

export type DevicePermissionKind =
  | "camera"
  | "contacts"
  | "location"
  | "microphone"
  | "notifications";

export type DevicePermissionIssue =
  | "denied"
  | "failed"
  | "unavailable"
  | "unsupported";

export type DevicePermissionGuidance = {
  title: string;
  description: string;
  steps: string[];
  rawMessage?: string;
  canOpenSettings: boolean;
  settingsTarget: PermissionSettingsTarget;
  primaryActionLabel: string;
  secondaryActionLabel?: string;
};

export class DevicePermissionError extends Error {
  readonly kind: DevicePermissionKind;
  readonly issue: DevicePermissionIssue;

  constructor(
    kind: DevicePermissionKind,
    message: string,
    issue: DevicePermissionIssue = "denied",
  ) {
    super(message);
    this.name = "DevicePermissionError";
    this.kind = kind;
    this.issue = issue;
  }
}

type BuildPermissionGuidanceOptions = {
  issue?: DevicePermissionIssue;
  platform?: PermissionPlatform;
  rawMessage?: string;
};

const settingsLabel = (platform: PermissionPlatform) => {
  if (platform === "ios") return "Open iPhone Settings";
  if (platform === "android") return "Open Android Settings";
  return "Open Browser Settings";
};

const sitePermissionSteps = (permission: string) => [
  "Open this site's permissions from the browser address bar.",
  `Set ${permission} to Allow.`,
  "Reload this page and try again.",
];

function permissionTarget(
  kind: DevicePermissionKind,
): PermissionSettingsTarget {
  if (kind === "notifications") return "notifications";
  return kind;
}

function issueAllowsSettings(issue: DevicePermissionIssue) {
  return issue === "denied" || issue === "failed";
}

function buildCameraGuidance(
  platform: PermissionPlatform,
  issue: DevicePermissionIssue,
): Omit<DevicePermissionGuidance, "canOpenSettings" | "rawMessage"> {
  if (issue === "unsupported") {
    return {
      title: "Camera Not Supported",
      description:
        "This device or browser cannot open the camera from Hiive right now.",
      steps: [
        "Use the Hiive mobile app or a browser with camera support.",
        "Make sure the page is running on a secure connection.",
      ],
      settingsTarget: "camera",
      primaryActionLabel: "Done",
    };
  }

  if (platform === "ios") {
    return {
      title: "Enable Camera",
      description:
        "Hiive needs camera access to capture photos and scan QR codes.",
      steps: [
        "Open iPhone Settings.",
        "Go to Hiive.",
        "Turn on Camera, then return to Hiive.",
      ],
      settingsTarget: "camera",
      primaryActionLabel: settingsLabel(platform),
      secondaryActionLabel: "Check Again",
    };
  }

  if (platform === "android") {
    return {
      title: "Enable Camera",
      description:
        "Hiive needs camera access to capture photos and scan QR codes.",
      steps: [
        "Open Android Settings.",
        "Go to Apps > Hiive > Permissions > Camera.",
        "Choose Allow only while using the app.",
      ],
      settingsTarget: "camera",
      primaryActionLabel: settingsLabel(platform),
      secondaryActionLabel: "Check Again",
    };
  }

  return {
    title: "Enable Camera",
    description:
      "Hiive needs camera access to capture photos and scan QR codes.",
    steps: sitePermissionSteps("Camera"),
    settingsTarget: "camera",
    primaryActionLabel: "Done",
    secondaryActionLabel: "Try Again",
  };
}

function buildContactsGuidance(
  platform: PermissionPlatform,
  issue: DevicePermissionIssue,
): Omit<DevicePermissionGuidance, "canOpenSettings" | "rawMessage"> {
  if (issue === "unsupported" || issue === "unavailable") {
    return {
      title: "Contacts Not Available",
      description:
        "This device or browser cannot share contacts with Hiive from here.",
      steps: [
        "Use the Hiive mobile app to enable contact sync.",
        "If you are already in the app, update Hiive and try again.",
      ],
      settingsTarget: "contacts",
      primaryActionLabel: "Done",
    };
  }

  if (platform === "ios") {
    return {
      title: "Enable Contacts",
      description:
        "Find friends who are already on Hiive. Your contact details stay private.",
      steps: [
        "Open iPhone Settings.",
        "Go to Apps > Hiive > Contacts.",
        "Allow Contacts, then return to Hiive.",
      ],
      settingsTarget: "contacts",
      primaryActionLabel: settingsLabel(platform),
      secondaryActionLabel: "Check Again",
    };
  }

  if (platform === "android") {
    return {
      title: "Enable Contacts",
      description:
        "Find friends who are already on Hiive. Your contact details stay private.",
      steps: [
        "Open Android Settings.",
        "Go to Apps > Hiive > Permissions > Contacts.",
        "Choose Allow, then return to Hiive.",
      ],
      settingsTarget: "contacts",
      primaryActionLabel: settingsLabel(platform),
      secondaryActionLabel: "Check Again",
    };
  }

  return {
    title: "Enable Contacts",
    description:
      "Hiive uses contact access to find social suggestions without uploading raw contact details.",
    steps: [
      "Use a browser that supports contact picking.",
      "Approve the browser contact picker when it appears.",
      "If the picker does not appear, use the Hiive mobile app.",
    ],
    settingsTarget: "contacts",
    primaryActionLabel: "Done",
    secondaryActionLabel: "Try Again",
  };
}

function buildLocationGuidance(
  platform: PermissionPlatform,
  issue: DevicePermissionIssue,
): Omit<DevicePermissionGuidance, "canOpenSettings" | "rawMessage"> {
  if (issue === "unsupported") {
    return {
      title: "Location Not Supported",
      description:
        "This device or browser cannot share location with Hiive from here.",
      steps: [
        "Use the Hiive mobile app or a browser with location support.",
        "Make sure the page is running on a secure connection.",
      ],
      settingsTarget: "location",
      primaryActionLabel: "Done",
    };
  }

  if (platform === "ios") {
    return {
      title: "Enable Location",
      description:
        "Hiive uses location for current-location sharing, maps, route tracking, distance, pace, and live progress.",
      steps: [
        "Open iPhone Settings.",
        "Go to Privacy & Security > Location Services > Hiive.",
        "Choose While Using the App and enable Precise Location.",
      ],
      settingsTarget: "location",
      primaryActionLabel: settingsLabel(platform),
      secondaryActionLabel: "Check Again",
    };
  }

  if (platform === "android") {
    return {
      title: "Enable Location",
      description:
        "Hiive uses location for current-location sharing, maps, route tracking, distance, pace, and live progress.",
      steps: [
        "Open Android Settings.",
        "Go to Apps > Hiive > Permissions > Location.",
        "Choose Allow only while using the app.",
        "Turn on Use precise location.",
      ],
      settingsTarget: "location",
      primaryActionLabel: settingsLabel(platform),
      secondaryActionLabel: "Check Again",
    };
  }

  return {
    title: "Enable Location",
    description:
      "Hiive uses location for current-location sharing, maps, route tracking, distance, pace, and live progress.",
    steps: sitePermissionSteps("Location"),
    settingsTarget: "location",
    primaryActionLabel: "Done",
    secondaryActionLabel: "Try Again",
  };
}

function buildMicrophoneGuidance(
  platform: PermissionPlatform,
  issue: DevicePermissionIssue,
): Omit<DevicePermissionGuidance, "canOpenSettings" | "rawMessage"> {
  if (issue === "unsupported") {
    return {
      title: "Microphone Not Supported",
      description:
        "This device or browser cannot record voice messages from Hiive right now.",
      steps: [
        "Use the Hiive mobile app or a browser with microphone support.",
        "Make sure the page is running on a secure connection.",
      ],
      settingsTarget: "microphone",
      primaryActionLabel: "Done",
    };
  }

  if (platform === "ios") {
    return {
      title: "Enable Microphone",
      description: "Hiive needs microphone access to record voice messages.",
      steps: [
        "Open iPhone Settings.",
        "Go to Hiive.",
        "Turn on Microphone, then return to Hiive.",
      ],
      settingsTarget: "microphone",
      primaryActionLabel: settingsLabel(platform),
      secondaryActionLabel: "Check Again",
    };
  }

  if (platform === "android") {
    return {
      title: "Enable Microphone",
      description: "Hiive needs microphone access to record voice messages.",
      steps: [
        "Open Android Settings.",
        "Go to Apps > Hiive > Permissions > Microphone.",
        "Choose Allow only while using the app.",
      ],
      settingsTarget: "microphone",
      primaryActionLabel: settingsLabel(platform),
      secondaryActionLabel: "Check Again",
    };
  }

  return {
    title: "Enable Microphone",
    description: "Hiive needs microphone access to record voice messages.",
    steps: sitePermissionSteps("Microphone"),
    settingsTarget: "microphone",
    primaryActionLabel: "Done",
    secondaryActionLabel: "Try Again",
  };
}

function buildNotificationGuidance(
  platform: PermissionPlatform,
  issue: DevicePermissionIssue,
): Omit<DevicePermissionGuidance, "canOpenSettings" | "rawMessage"> {
  if (issue === "unsupported") {
    return {
      title: "Notifications Not Supported",
      description:
        "This device or browser cannot receive Hiive push notifications from this surface.",
      steps: [
        "Use a supported browser on a secure connection.",
        "On iOS browsers, install Hiive to your Home Screen.",
        "On native apps, update Hiive and try again.",
      ],
      settingsTarget: "notifications",
      primaryActionLabel: "Done",
    };
  }

  if (platform === "ios") {
    return {
      title: "Enable Notifications",
      description:
        "Hiive uses notifications for messages, buddy requests, bookings, game reminders, and live tracking alerts.",
      steps: [
        "Open iPhone Settings.",
        "Go to Notifications > Hiive.",
        "Enable Allow Notifications, then return to Hiive.",
      ],
      settingsTarget: "notifications",
      primaryActionLabel: settingsLabel(platform),
      secondaryActionLabel: "Check Again",
    };
  }

  if (platform === "android") {
    return {
      title: "Enable Notifications",
      description:
        "Hiive uses notifications for messages, buddy requests, bookings, game reminders, and live tracking alerts.",
      steps: [
        "Open Android Settings.",
        "Go to Apps > Hiive > Notifications.",
        "Enable notifications, then return to Hiive.",
      ],
      settingsTarget: "notifications",
      primaryActionLabel: settingsLabel(platform),
      secondaryActionLabel: "Check Again",
    };
  }

  return {
    title: "Enable Browser Notifications",
    description:
      "Hiive uses notifications for messages, buddy requests, bookings, game reminders, and live tracking alerts.",
    steps: sitePermissionSteps("Notifications"),
    settingsTarget: "notifications",
    primaryActionLabel: "Done",
    secondaryActionLabel: "Try Again",
  };
}

export function buildPermissionGuidance(
  kind: DevicePermissionKind,
  options: BuildPermissionGuidanceOptions = {},
): DevicePermissionGuidance {
  const platform = options.platform ?? getPermissionPlatform();
  const issue = options.issue ?? "denied";
  const builder =
    kind === "camera"
      ? buildCameraGuidance
      : kind === "contacts"
        ? buildContactsGuidance
        : kind === "location"
          ? buildLocationGuidance
          : kind === "microphone"
            ? buildMicrophoneGuidance
            : buildNotificationGuidance;
  const guidance = builder(platform, issue);
  const target = permissionTarget(kind);
  const canOpenSettings =
    issueAllowsSettings(issue) && canOpenPermissionSettings(target);

  return {
    ...guidance,
    settingsTarget: target,
    canOpenSettings,
    primaryActionLabel: canOpenSettings
      ? guidance.primaryActionLabel
      : guidance.primaryActionLabel === settingsLabel(platform)
        ? "Done"
        : guidance.primaryActionLabel,
    rawMessage: options.rawMessage,
  };
}

export function isPermissionDeniedError(error: unknown): boolean {
  const maybeError = error as {
    code?: unknown;
    message?: unknown;
    name?: unknown;
  };
  const rawCode = maybeError?.code;
  const code = String(maybeError?.code ?? "").toLowerCase();
  const message = String(maybeError?.message ?? "").toLowerCase();
  const name = String(maybeError?.name ?? "").toLowerCase();

  return (
    rawCode === 1 ||
    name === "notallowederror" ||
    name === "permissiondeniederror" ||
    code.includes("permission") ||
    code.includes("denied") ||
    message.includes("permission") ||
    message.includes("denied") ||
    message.includes("not authorized") ||
    message.includes("not authorised")
  );
}
